import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ImageOff } from 'lucide-react';
import { useStoreApi } from '@xyflow/react';
import type { LightSourceParams } from '@/types';
import { clampHorizontalAngle, clampVerticalAngle } from './lightingPresets';

interface LightingViewportProps {
  imageUrl?: string;
  lights: LightSourceParams[];
  sceneRotation: number;
  activeLightId: string;
  onLightAngleChange: (lightId: string, horizontal: number, vertical: number) => void;
  onSceneRotationChange: (rotation: number) => void;
  onActiveLightChange: (lightId: string) => void;
}

const BG = 0x0a0a0b;
// 图片平面：方形基面（宽度随纹理宽高比缩放），中心固定高度（对齐多角度视口基线）
const PLANE_SIZE = 1.5;
// 板厚：侧视/背视可辨识（照片板效果）
const PLANE_THICKNESS = 0.07;
// 已加载图片缓存（imageUrl → HTMLImageElement）：面板重开/换回同图命中缓存时同步建纹理直接上屏，
// 避免「灰板一帧 → 贴图缓动」造成的打开闪烁；超出容量按插入序淘汰
const imageCache = new Map<string, HTMLImageElement>();
const IMAGE_CACHE_MAX = 12;
const SUBJECT_Y = 0.975;
// 灯点轨道球面半径（方案 §4：R ≈ 2.2）
const ORBIT_RADIUS = 2.2;
// 相机：俯仰锁定固定微俯视角（~12°，略压低以更多正对主体），仅左右旋转（由 rotGroup 承载）+ 滚轮推拉
const CAMERA_PITCH = 12;
const CAMERA_DIST_DEFAULT = 5.6;
const CAMERA_DIST_MIN = 3.4;
const CAMERA_DIST_MAX = 10;
// 场景旋转灵敏度（度/像素）
const ROTATE_SENSITIVITY = 0.35;
// 拖拽期间向 React 回调发射更新的最小间隔（ms）：3D 位姿本地即时应用保证跟手，仅数值回显降频
const DRAG_EMIT_INTERVAL = 80;
// 灯点屏幕命中半径（px）
const BULB_HIT_PX = 16;
// 渲染像素比：多层加法混合光锥属填充率密集型，倍率过高在中低端 GPU 必卡（2.5 已够锐利）
const MAX_PIXEL_RATIO = 2.5;
const MIN_PIXEL_RATIO = 1.5;
// 画布净拉伸倍数 = 实测视觉宽 / 布局宽（InverseScaleBox 反向缩放补偿）
const measureStretch = (el: HTMLElement): number => {
  const layout = el.offsetWidth;
  if (layout <= 0) return 1;
  const stretched = el.getBoundingClientRect().width;
  if (stretched <= 0) return 1;
  return stretched / layout;
};
const targetPixelRatio = (el: HTMLElement): number =>
  Math.min(Math.max((window.devicePixelRatio || 1) * measureStretch(el), MIN_PIXEL_RATIO), MAX_PIXEL_RATIO);

const deg2rad = (d: number) => (d * Math.PI) / 180;

/** 单盏灯的 3D 模型集合（灯头/渐变光斑锥/双层光锥/支撑线/反光罩） */
interface LightEntry {
  group: import('three').Group;
  bulb: import('three').Mesh;
  glowCone: import('three').Mesh;
  coneInner: import('three').Mesh;
  coneOuter: import('three').Mesh;
  line: import('three').Line;
  shade: import('three').Mesh;
}

/** 灯位球面坐标 → 局部坐标（rotGroup 局部系：中心 (0, SUBJECT_Y, 0)） */
const lightLocalPos = (THREE: typeof import('three'), hDeg: number, vDeg: number): import('three').Vector3 => {
  const h = deg2rad(hDeg);
  const v = deg2rad(vDeg);
  return new THREE.Vector3(
    ORBIT_RADIUS * Math.sin(h) * Math.cos(v),
    SUBJECT_Y + ORBIT_RADIUS * Math.sin(v),
    ORBIT_RADIUS * Math.cos(h) * Math.cos(v),
  );
};

/** 单灯位姿应用：位置/反光罩朝向/双层光锥/渐变光斑/支撑线（拖拽时本地直接调用，绕过 React 回路） */
const applyLightPose = (
  THREE: typeof import('three'),
  entry: LightEntry,
  l: { horizontalAngle?: number; verticalAngle?: number; intensity?: number; color?: string; lightType?: string },
) => {
  const h = l.horizontalAngle ?? -45;
  const v = l.verticalAngle ?? 60;
  const intensity = l.intensity ?? 5;
  const color = new THREE.Color(l.color || '#FFFFFF');
  const bulbPos = lightLocalPos(THREE, h, v);
  const center = new THREE.Vector3(0, SUBJECT_Y, 0);

  entry.bulb.position.copy(bulbPos);

  // 反光罩：罩顶指向外侧（背离主体），开口朝主体
  entry.shade.position.copy(bulbPos);
  entry.shade.quaternion.setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    bulbPos.clone().sub(center).normalize(),
  );

  // 光锥：锥顶接灯头、锥底覆盖图片平面；硬光 = 窄而实，柔光 = 宽而羽化（两层锥体近似）
  const dir = bulbPos.clone().sub(center).normalize();
  const len = bulbPos.distanceTo(center);
  const mid = center.clone().add(bulbPos).multiplyScalar(0.5);
  const innerR = l.lightType === 'hard' ? 0.26 : 0.5;
  const outerR = l.lightType === 'hard' ? 0.46 : 1.0;
  const innerOpacity = 0.1 + intensity * 0.018;
  const innerMat = entry.coneInner.material as import('three').MeshBasicMaterial;
  const outerMat = entry.coneOuter.material as import('three').MeshBasicMaterial;
  entry.coneInner.position.copy(mid);
  entry.coneInner.scale.set(innerR, len, innerR);
  entry.coneInner.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
  innerMat.color.copy(color);
  innerMat.opacity = innerOpacity;
  entry.coneOuter.position.copy(mid);
  entry.coneOuter.scale.set(outerR, len, outerR);
  entry.coneOuter.quaternion.copy(entry.coneInner.quaternion);
  outerMat.color.copy(color);
  outerMat.opacity = innerOpacity * 0.45;

  // 光斑：渐变光锥（锥顶=灯头端最亮，沿光束渐隐），长度约为光束一半
  const glowLen = len * 0.55;
  const glowR = (l.lightType === 'hard' ? 0.3 : 0.45) * (0.7 + intensity * 0.06);
  const glowMat = entry.glowCone.material as import('three').MeshBasicMaterial;
  entry.glowCone.position.copy(bulbPos.clone().lerp(center, 0.275));
  entry.glowCone.scale.set(glowR, glowLen, glowR);
  entry.glowCone.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
  glowMat.color.copy(color);
  glowMat.opacity = Math.min(0.28 + intensity * 0.045, 0.55);

  // 支撑线：灯头 → 图片中心
  const posAttr = entry.line.geometry.getAttribute('position') as import('three').BufferAttribute;
  posAttr.setXYZ(0, center.x, center.y, center.z);
  posAttr.setXYZ(1, bulbPos.x, bulbPos.y, bulbPos.z);
  posAttr.needsUpdate = true;
  entry.line.geometry.computeBoundingSphere();
};

export const LightingViewport = memo<LightingViewportProps>(({
  imageUrl,
  lights,
  sceneRotation,
  activeLightId,
  onLightAngleChange,
  onSceneRotationChange,
  onActiveLightChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasHostRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<import('three').WebGLRenderer | null>(null);
  const sceneRef = useRef<import('three').Scene | null>(null);
  const cameraRef = useRef<import('three').PerspectiveCamera | null>(null);
  const rotGroupRef = useRef<import('three').Group | null>(null);
  const lightsGroupRef = useRef<import('three').Group | null>(null);
  const planeRef = useRef<import('three').Mesh | null>(null);
  const glowTextureRef = useRef<import('three').Texture | null>(null);
  const beamTextureRef = useRef<import('three').Texture | null>(null);
  const threeRef = useRef<typeof import('three') | null>(null);
  const raycasterRef = useRef<import('three').Raycaster | null>(null);
  const scratchVecRef = useRef<import('three').Vector3 | null>(null);
  const scratchVec2Ref = useRef<import('three').Vector3 | null>(null);
  const scratchNdcRef = useRef<import('three').Vector2 | null>(null);
  const centerWorldRef = useRef<import('three').Vector3 | null>(null);
  const sphereRef = useRef<import('three').Sphere | null>(null);
  const lightEntriesRef = useRef<Map<string, LightEntry>>(new Map());
  const sizeRef = useRef({ width: 560, height: 390 });
  const distRef = useRef(CAMERA_DIST_DEFAULT);
  // ReactFlow store：画布缩放订阅用（Hook 只能在组件顶层调用）
  const store = useStoreApi();

  // 最新 props 的 ref（事件处理器内读取，避免闭包过期）
  const lightsRef = useRef(lights);
  lightsRef.current = lights;
  const sceneRotationRef = useRef(sceneRotation);
  sceneRotationRef.current = sceneRotation;
  const activeLightIdRef = useRef(activeLightId);
  activeLightIdRef.current = activeLightId;
  const onLightAngleChangeRef = useRef(onLightAngleChange);
  onLightAngleChangeRef.current = onLightAngleChange;
  const onSceneRotationChangeRef = useRef(onSceneRotationChange);
  onSceneRotationChangeRef.current = onSceneRotationChange;
  const onActiveLightChangeRef = useRef(onActiveLightChange);
  onActiveLightChangeRef.current = onActiveLightChange;

  const [ready, setReady] = useState(false);
  const [textureFailed, setTextureFailed] = useState(false);
  const [hoverBulb, setHoverBulb] = useState(false);
  const dragStateRef = useRef<{
    mode: 'light' | 'rotate';
    lightId?: string;
    lastX: number;
    lastEmit: number;
    rotBase?: number;
    pending?: { lightId: string; h: number; v: number };
    pendingRot?: number;
  } | null>(null);
  // 拖拽期间缓存的视口 rect（避免高频 pointermove 反复 getBoundingClientRect 触发强制布局）
  const dragRectRef = useRef<DOMRect | null>(null);

  const rafRef = useRef<number | null>(null);
  const requestRender = useCallback(() => {
    // rAF 合帧：同一显示帧内多次触发只渲染一次（高回报率鼠标连发 pointermove 时避免重复整帧渲染）
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const renderer = rendererRef.current;
      const scene = sceneRef.current;
      const camera = cameraRef.current;
      if (!renderer || !scene || !camera) return;
      renderer.render(scene, camera);
    });
  }, []);

  /** 相机位姿：俯仰锁定（固定 17° 微俯视），仅距离可变（滚轮推拉） */
  const applyCameraPose = useCallback(() => {
    const camera = cameraRef.current;
    if (!camera) return;
    const pitch = deg2rad(CAMERA_PITCH);
    const dist = distRef.current;
    camera.position.set(0, SUBJECT_Y + dist * Math.sin(pitch), dist * Math.cos(pitch));
    camera.lookAt(0, SUBJECT_Y, 0);
  }, []);

  /** 场景旋转：rotGroup 绕竖直轴（唯一视角自由度，俯仰锁定无平移） */
  const applySceneRotation = useCallback(() => {
    const rotGroup = rotGroupRef.current;
    if (!rotGroup) return;
    rotGroup.rotation.y = deg2rad(sceneRotationRef.current);
  }, [sceneRotationRef]);

  /** 同步灯模型集合：增删灯 + 位置/颜色/强度/硬柔形态更新（方案 §4 参数联动表） */
  const syncLights = useCallback(() => {
    const THREE = threeRef.current;
    const rotGroup = rotGroupRef.current;
    if (!THREE || !rotGroup) return;
    const entries = lightEntriesRef.current;
    const seen = new Set<string>();

    for (const l of lightsRef.current) {
      seen.add(l.id);
      let entry = entries.get(l.id);
      if (!entry) {
        const group = new THREE.Group();
        const bulb = new THREE.Mesh(
          new THREE.SphereGeometry(0.07, 14, 14),
          new THREE.MeshBasicMaterial({ color: 0xffffff }),
        );
        // 渐变光斑锥：与光锥同轴（锥顶=灯头端），贴图沿长度方向亮→透明，替代圆形光晕
        const coneGeo = new THREE.ConeGeometry(1, 1, 24, 1, true);
        const glowCone = new THREE.Mesh(
          coneGeo,
          new THREE.MeshBasicMaterial({
            map: glowTextureRef.current || undefined,
            color: 0xffffff,
            transparent: true,
            opacity: 0.35,
            side: THREE.FrontSide,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          }),
        );
        const coneInner = new THREE.Mesh(
          coneGeo,
          new THREE.MeshBasicMaterial({
            map: beamTextureRef.current || undefined,
            color: 0xffffff, transparent: true, opacity: 0.2,
            side: THREE.FrontSide, blending: THREE.AdditiveBlending, depthWrite: false,
          }),
        );
        const coneOuter = new THREE.Mesh(
          coneGeo.clone(),
          new THREE.MeshBasicMaterial({
            map: beamTextureRef.current || undefined,
            color: 0xffffff, transparent: true, opacity: 0.09,
            side: THREE.FrontSide, blending: THREE.AdditiveBlending, depthWrite: false,
          }),
        );
        const lineGeo = new THREE.BufferGeometry();
        lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(6), 3));
        const line = new THREE.Line(
          lineGeo,
          new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.22 }),
        );
        line.frustumCulled = false;
        // 反光罩：碗形外壳罩住灯头背面（开口朝主体），深灰（比壳体原色略浅），小张角少遮灯泡
        const shade = new THREE.Mesh(
          new THREE.SphereGeometry(0.125, 18, 12, 0, Math.PI * 2, 0, Math.PI * 0.5278),
          new THREE.MeshBasicMaterial({ color: 0x3F3F46, side: THREE.DoubleSide }),
        );
        group.add(bulb, glowCone, coneInner, coneOuter, line, shade);
        rotGroup.add(group);
        entry = { group, bulb, glowCone, coneInner, coneOuter, line, shade };
        entries.set(l.id, entry);
      }

      // 位置与形态更新（含反光罩/双层光锥/渐变光斑/支撑线）
      applyLightPose(THREE, entry, l);
    }

    // 移除已删除的灯
    for (const [id, entry] of entries) {
      if (seen.has(id)) continue;
      rotGroup.remove(entry.group);
      (entry.bulb.geometry as import('three').BufferGeometry).dispose();
      (entry.bulb.material as import('three').Material).dispose();
      (entry.glowCone.material as import('three').Material).dispose();
      (entry.coneInner.geometry as import('three').BufferGeometry).dispose();
      (entry.coneInner.material as import('three').Material).dispose();
      (entry.coneOuter.geometry as import('three').BufferGeometry).dispose();
      (entry.coneOuter.material as import('three').Material).dispose();
      entry.line.geometry.dispose();
      (entry.line.material as import('three').Material).dispose();
      entry.shade.geometry.dispose();
      (entry.shade.material as import('three').MeshBasicMaterial).dispose();
      entries.delete(id);
    }
  }, [lightLocalPos]);

  // three.js 场景初始化（面板首开才动态加载）
  useEffect(() => {
    let cancelled = false;
    // 罗盘贴图引用（卸载时显式释放，材质遍历释放不覆盖 map）
    let compassTexture: import('three').Texture | null = null;
    const container = containerRef.current;
    const canvasHost = canvasHostRef.current;
    if (!container || !canvasHost) return;

    (async () => {
      const {
        Scene,
        PerspectiveCamera,
        WebGLRenderer,
        Group,
        Mesh,
        MeshBasicMaterial,
        BoxGeometry,
        GridHelper,
        PlaneGeometry,
        TextureLoader,
        Texture,
        SRGBColorSpace,
        DoubleSide,
        Raycaster,
        Vector3,
        Vector2,
        Sphere,
        CanvasTexture,
      } = await import('three');
      if (cancelled) return;
      const ns = await import('three');
      threeRef.current = ns;

      const width = container.clientWidth || 560;
      const height = container.clientHeight || 390;
      sizeRef.current = { width, height };

      const renderer = new WebGLRenderer({ antialias: true });
      renderer.setPixelRatio(targetPixelRatio(container));
      renderer.setSize(width, height);
      renderer.setClearColor(BG);
      renderer.domElement.style.display = 'block';
      canvasHost.appendChild(renderer.domElement);

      const scene = new Scene();
      const camera = new PerspectiveCamera(45, width / height, 0.1, 100);

      // 旋转组：图片平面 + 全部灯模型 + 地面网格（sceneRotation 唯一视角自由度）
      const rotGroup = new Group();
      scene.add(rotGroup);

      // 地面网格随场景旋转（配合轴线与方位标注，旋转才可感知）
      const grid = new GridHelper(6, 24, 0x232326, 0x1a1a1d);
      rotGroup.add(grid);

      // 方位罗盘：渐隐箭头标线 + 前/后/左/右 文字（SVG 贴图，蓝=前后 Z 轴、红=左右 X 轴），微抬避开网格线
      const compassGeo = new PlaneGeometry(6, 6);
      compassGeo.rotateX(-Math.PI / 2);
      const compassMat = new MeshBasicMaterial({ transparent: true, opacity: 0.9, depthWrite: false });
      const compass = new Mesh(compassGeo, compassMat);
      compass.visible = false;
      compass.position.y = 0.008;
      rotGroup.add(compass);
      new TextureLoader().load(
        '/icons/lighting/compass-arrows.svg',
        (texture) => {
          if (cancelled) {
            texture.dispose();
            return;
          }
          compassTexture = texture;
          texture.colorSpace = SRGBColorSpace;
          compassMat.map = texture;
          compassMat.needsUpdate = true;
          compass.visible = true;
          requestRender();
        },
      );

      // 图片板：带厚度盒子（背光灯箱效果）——正面全亮贴图，背面同图压暗（透光感），侧面贴图挤成窄条呈亮边
      const sideMat = new MeshBasicMaterial({ color: 0x26262a });
      const backMat = new MeshBasicMaterial({ color: 0x262629 });
      const frontMat = new MeshBasicMaterial({ color: 0x2c2c2e, side: DoubleSide });
      const plane = new Mesh(
        new BoxGeometry(PLANE_SIZE, PLANE_SIZE, PLANE_THICKNESS),
        [sideMat, sideMat, sideMat, sideMat, frontMat, backMat],
      );
      plane.position.y = SUBJECT_Y;
      rotGroup.add(plane);

      // 渐变光斑贴图：沿锥轴渐变（画布顶部=锥顶/灯头端=最亮，向下渐隐到透明），全部灯共享
      const glowCanvas = document.createElement('canvas');
      glowCanvas.width = 128;
      glowCanvas.height = 128;
      const gctx = glowCanvas.getContext('2d')!;
      const gradient = gctx.createLinearGradient(0, 0, 0, 128);
      gradient.addColorStop(0, 'rgba(255,255,255,0.95)');
      gradient.addColorStop(0.4, 'rgba(255,255,255,0.35)');
      gradient.addColorStop(1, 'rgba(255,255,255,0)');
      gctx.fillStyle = gradient;
      gctx.fillRect(0, 0, 128, 128);
      const glowTexture = new CanvasTexture(glowCanvas);
      glowTextureRef.current = glowTexture;

      // 光线渐变贴图：较缓的沿轴渐变（灯头端亮、底部保留部分光），双层光锥共享
      const beamCanvas = document.createElement('canvas');
      beamCanvas.width = 8;
      beamCanvas.height = 128;
      const bctx = beamCanvas.getContext('2d')!;
      const beamGradient = bctx.createLinearGradient(0, 0, 0, 128);
      beamGradient.addColorStop(0, 'rgba(255,255,255,1)');
      beamGradient.addColorStop(0.5, 'rgba(255,255,255,0.5)');
      beamGradient.addColorStop(1, 'rgba(255,255,255,0.18)');
      bctx.fillStyle = beamGradient;
      bctx.fillRect(0, 0, 8, 128);
      const beamTexture = new CanvasTexture(beamCanvas);
      beamTextureRef.current = beamTexture;

      rendererRef.current = renderer;
      sceneRef.current = scene;
      cameraRef.current = camera;
      rotGroupRef.current = rotGroup;
      lightsGroupRef.current = rotGroup;
      planeRef.current = plane;
      raycasterRef.current = new Raycaster();
      scratchVecRef.current = new Vector3();
      scratchVec2Ref.current = new Vector3();
      scratchNdcRef.current = new Vector2();
      centerWorldRef.current = new Vector3(0, SUBJECT_Y, 0);
      sphereRef.current = new Sphere(centerWorldRef.current, ORBIT_RADIUS);

      applyCameraPose();
      applySceneRotation();

      // 面板重开/换回看过的图：建板时同步命中缓存直接贴图，首帧即成品
      // （初始化末尾 requestRender 会画出第一帧；若等 ready 后的贴图 effect 异步上屏，灰板会先闪现）
      const cachedImage = imageUrl ? imageCache.get(imageUrl) : undefined;
      if (cachedImage && cachedImage.complete && cachedImage.naturalWidth > 0) {
        const texture = new Texture(cachedImage);
        texture.colorSpace = SRGBColorSpace;
        texture.needsUpdate = true;
        frontMat.map = texture;
        frontMat.color.set(0xffffff);
        backMat.map = texture;
        backMat.color.set(0x71717a);
        sideMat.map = texture;
        sideMat.color.set(0x909098);
        loadedTextureRef.current = texture;
        loadedTextureUrlRef.current = imageUrl ?? null;
        // 板宽按图片比例展开（clamp 0.5~2.2，对齐网络加载路径）
        const iw = cachedImage.naturalWidth || cachedImage.width;
        const ih = cachedImage.naturalHeight || cachedImage.height;
        if (iw > 0 && ih > 0) plane.scale.set(Math.max(0.5, Math.min(2.2, iw / ih)), 1, 1);
      }

      setReady(true);
      syncLights();
      requestRender();
    })();

    return () => {
      cancelled = true;
      const renderer = rendererRef.current;
      const scene = sceneRef.current;
      if (scene) {
        scene.traverse((obj) => {
          const mesh = obj as import('three').Mesh;
          if (mesh.geometry) mesh.geometry.dispose();
          const material = mesh.material as import('three').Material | import('three').Material[] | undefined;
          if (Array.isArray(material)) material.forEach((m) => m.dispose());
          else if (material) material.dispose();
        });
      }
      glowTextureRef.current?.dispose();
      glowTextureRef.current = null;
      beamTextureRef.current?.dispose();
      beamTextureRef.current = null;
      compassTexture?.dispose();
      // 释放已加载的图片纹理（注意在置空 plane.material 引用前释放）
      loadedTextureRef.current?.dispose();
      loadedTextureRef.current = null;
      lightEntriesRef.current.clear();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (renderer) {
        renderer.dispose();
        renderer.forceContextLoss();
        renderer.domElement.remove();
      }
      rendererRef.current = null;
      sceneRef.current = null;
      cameraRef.current = null;
      rotGroupRef.current = null;
      planeRef.current = null;
      threeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 图片纹理加载（跨域失败降级灰面占位）；就绪后做缓动过渡，消除「占位灰板→贴图」跳闪
  const loadedTextureRef = useRef<import('three').Texture | null>(null);
  // 当前贴图对应的图片 URL：建板时已同步贴图的同 URL 不重复应用（贴图 effect 直接跳过）
  const loadedTextureUrlRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ready) return;
    let disposed = false;
    let rafId = 0;
    const plane = planeRef.current;
    if (!plane) return;
    /** 三面同步贴图：正面全亮 / 背面压暗透光 / 侧面亮边（颜色乘法压暗贴图） */
    const applyBoardTexture = (texture: import('three').Texture | null) => {
      const mats = plane.material as import('three').Material[];
      const front = mats[4] as import('three').MeshBasicMaterial;
      const back = mats[5] as import('three').MeshBasicMaterial;
      const side = mats[0] as import('three').MeshBasicMaterial;
      front.map = texture;
      front.color.set(texture ? 0xffffff : 0x2c2c2e);
      back.map = texture;
      back.color.set(texture ? 0x71717a : 0x262629);
      side.map = texture;
      side.color.set(texture ? 0x909098 : 0x26262a);
      for (const m of mats) (m as import('three').MeshBasicMaterial).needsUpdate = true;
    };
    if (!imageUrl) {
      applyBoardTexture(null);
      plane.scale.set(1, 1, 1);
      loadedTextureUrlRef.current = null;
      setTextureFailed(false);
      requestRender();
      return;
    }
    /** 贴图就绪过渡：三面颜色与板宽从当前值缓动到贴图态（贴图×起点色≈占位灰，首帧衔接无跳变） */
    const startBoardTransition = (THREE: typeof import('three'), texture: import('three').Texture, aspect: number) => {
      const mats = plane.material as import('three').Material[];
      const front = mats[4] as import('three').MeshBasicMaterial;
      const back = mats[5] as import('three').MeshBasicMaterial;
      const side = mats[0] as import('three').MeshBasicMaterial;
      const fromFront = front.color.clone();
      const fromBack = back.color.clone();
      const fromSide = side.color.clone();
      const fromScale = plane.scale.x;
      applyBoardTexture(texture);
      const toFront = new THREE.Color(0xffffff);
      const toBack = new THREE.Color(0x71717a);
      const toSide = new THREE.Color(0x909098);
      const start = performance.now();
      const tick = (now: number) => {
        if (disposed || planeRef.current !== plane) return;
        const t = Math.min(1, (now - start) / 300);
        const e = t * t * (3 - 2 * t);
        plane.scale.set(fromScale + (aspect - fromScale) * e, 1, 1);
        front.color.copy(fromFront).lerp(toFront, e);
        back.color.copy(fromBack).lerp(toBack, e);
        side.color.copy(fromSide).lerp(toSide, e);
        requestRender();
        if (t < 1) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    };
    // 建板时已同步贴过同图（缓存命中初始化）：无需重复应用
    if (loadedTextureUrlRef.current === imageUrl) return;

    // 图片切换命中缓存（换回看过的图）：同步建纹理直接上屏，跳过灰板帧与缓动
    const cachedImage = imageCache.get(imageUrl);
    if (cachedImage && cachedImage.complete && cachedImage.naturalWidth > 0) {
      // 刷新插入序（Map 按插入序淘汰，删后重设等效 LRU 触碰）
      imageCache.delete(imageUrl);
      imageCache.set(imageUrl, cachedImage);
      import('three').then((THREE) => {
        if (disposed || planeRef.current !== plane) return;
        const texture = new THREE.Texture(cachedImage);
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;
        loadedTextureRef.current?.dispose();
        loadedTextureRef.current = texture;
        loadedTextureUrlRef.current = imageUrl;
        applyBoardTexture(texture);
        const iw = cachedImage.naturalWidth || cachedImage.width;
        const ih = cachedImage.naturalHeight || cachedImage.height;
        if (iw > 0 && ih > 0) plane.scale.set(Math.max(0.5, Math.min(2.2, iw / ih)), 1, 1);
        setTextureFailed(false);
        requestRender();
      });
      return () => {
        disposed = true;
        if (rafId) cancelAnimationFrame(rafId);
      };
    }

    import('three').then((THREE) => {
      if (disposed) return;
      const img = new Image();
      img.onload = () => {
        if (disposed || planeRef.current !== plane) return;
        const texture = new THREE.Texture(img);
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;
        loadedTextureRef.current?.dispose();
        loadedTextureRef.current = texture;
        loadedTextureUrlRef.current = imageUrl;
        // 按原始宽高比缓动板宽（宽度 clamp 0.5~2.2，防极端比例溢出视口）
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;
        // 首次加载成功回填缓存：面板重开/换回同图走同步命中路径
        if (width > 0 && height > 0) {
          imageCache.set(imageUrl, img);
          if (imageCache.size > IMAGE_CACHE_MAX) {
            const oldest = imageCache.keys().next().value;
            if (oldest !== undefined) imageCache.delete(oldest);
          }
        }
        const aspect = width > 0 && height > 0 ? Math.max(0.5, Math.min(2.2, width / height)) : 1;
        startBoardTransition(THREE, texture, aspect);
        setTextureFailed(false);
        requestRender();
      };
      img.onerror = () => {
        if (disposed) return;
        setTextureFailed(true);
        requestRender();
      };
      img.src = imageUrl;
    });
    return () => {
      disposed = true;
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [ready, imageUrl, requestRender]);

  // props → 场景同步：灯集合 / 场景旋转（拖拽期间本地直更为准，跳过滞后的节流值回灌，松手后最终值对齐）
  useEffect(() => {
    if (!ready) return;
    if (dragStateRef.current === null) {
      syncLights();
      applySceneRotation();
    }
    requestRender();
  }, [ready, lights, sceneRotation, syncLights, applySceneRotation, requestRender]);

  // 画布缩放 → InverseScaleBox 反向倍数变化，背衬分辨率跟随补足（直订 ReactFlow store）
  useEffect(() => {
    let prevZoom = store.getState().transform[2];
    const applyRatio = () => {
      const renderer = rendererRef.current;
      const el = containerRef.current;
      if (!ready || !renderer || !el) return;
      renderer.setPixelRatio(targetPixelRatio(el));
      requestRender();
    };
    applyRatio();
    const unsub = store.subscribe((state) => {
      const zoom = state.transform[2];
      if (zoom !== prevZoom) {
        prevZoom = zoom;
        applyRatio();
      }
    });
    return unsub;
  }, [ready, requestRender, store]);

  // 容器尺寸变化 → 更新相机与画布
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !ready) return;
    const observer = new ResizeObserver(() => {
      const renderer = rendererRef.current;
      const camera = cameraRef.current;
      const width = container.clientWidth;
      const height = container.clientHeight;
      if (!renderer || !camera || width === 0 || height === 0) return;
      sizeRef.current = { width, height };
      renderer.setPixelRatio(targetPixelRatio(container));
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      requestRender();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [ready, requestRender]);

  /** 指针 → NDC（拖拽期间复用 pointerdown 缓存的 rect） */
  const toNDC = useCallback((e: PointerEvent | React.PointerEvent): { x: number; y: number } => {
    const rect = dragRectRef.current ?? containerRef.current!.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
      y: -((e.clientY - rect.top) / rect.height) * 2 + 1,
    };
  }, []);

  /** 命中测试：返回屏幕距离最近（且 < BULB_HIT_PX）的灯 id */
  const hitTestBulb = useCallback((clientX: number, clientY: number): string | null => {
    const camera = cameraRef.current;
    const container = containerRef.current;
    if (!camera || !container) return null;
    const rect = container.getBoundingClientRect();
    let bestId: string | null = null;
    let bestDist = Infinity;
    const v = scratchVec2Ref.current!;
    for (const [id, entry] of lightEntriesRef.current) {
      entry.bulb.getWorldPosition(v);
      v.project(camera);
      const sx = rect.left + ((v.x + 1) / 2) * rect.width;
      const sy = rect.top + ((1 - v.y) / 2) * rect.height;
      const d = Math.hypot(sx - clientX, sy - clientY);
      if (d < BULB_HIT_PX && d < bestDist) {
        bestDist = d;
        bestId = id;
      }
    }
    return bestId;
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const container = containerRef.current;
    if (!container || !ready) return;
    container.setPointerCapture(e.pointerId);
    dragRectRef.current = container.getBoundingClientRect();
    const hitId = hitTestBulb(e.clientX, e.clientY);
    if (hitId) {
      onActiveLightChangeRef.current(hitId);
      dragStateRef.current = { mode: 'light', lightId: hitId, lastX: e.clientX, lastEmit: 0 };
    } else {
      // 旋转基点：拖拽期间独立累积（sceneRotationRef 要等节流回调 React 提交后才刷新，直接用它会迟滞）
      dragStateRef.current = { mode: 'rotate', lastX: e.clientX, lastEmit: 0, rotBase: sceneRotationRef.current };
    }
  }, [ready, hitTestBulb]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const drag = dragStateRef.current;
    if (!drag) {
      // 悬停态光标反馈
      if (ready) {
        const hover = !!hitTestBulb(e.clientX, e.clientY);
        setHoverBulb((prev) => (prev === hover ? prev : hover));
      }
      return;
    }
    if (drag.mode === 'rotate') {
      // 仅左右旋转场景（垂直方向无效——俯仰锁定，方案 §3 交互要点 2）；基点独立累积防迟滞
      const deltaX = e.clientX - drag.lastX;
      drag.lastX = e.clientX;
      const next = clampHorizontalAngle((drag.rotBase ?? 0) + deltaX * ROTATE_SENSITIVITY);
      drag.rotBase = next;
      // 旋转立即本地应用（不经过 React 回路），场景跟手
      const g = rotGroupRef.current;
      if (g) {
        g.rotation.y = deg2rad(next);
        requestRender();
      }
      drag.pendingRot = next;
      const now = performance.now();
      if (now - drag.lastEmit >= DRAG_EMIT_INTERVAL) {
        drag.lastEmit = now;
        onSceneRotationChangeRef.current(next);
        drag.pendingRot = undefined;
      }
      return;
    }
    // 拖灯：指针射线与轨道球面求交 → 反解水平/垂直角（绝对跟随）
    const camera = cameraRef.current;
    const raycaster = raycasterRef.current;
    const sphere = sphereRef.current;
    const rotGroup = rotGroupRef.current;
    const target = scratchVecRef.current;
    const ndcVec = scratchNdcRef.current;
    if (!camera || !raycaster || !sphere || !rotGroup || !target || !ndcVec || !drag.lightId) return;
    const ndc = toNDC(e);
    ndcVec.set(ndc.x, ndc.y);
    raycaster.setFromCamera(ndcVec, camera);
    // 命中轨道球面；指针越出球面投影边缘时夹取到球面最近点（消灭极端位置的拖拽死区）
    if (!raycaster.ray.intersectSphere(sphere, target)) {
      raycaster.ray.closestPointToPoint(sphere.center, target);
      const off = target.clone().sub(sphere.center);
      if (off.lengthSq() < 1e-6) off.set(0, 0, 1);
      target.copy(sphere.center).add(off.normalize().multiplyScalar(ORBIT_RADIUS));
    }
    {
      const local = rotGroup.worldToLocal(target.clone());
      const x = local.x;
      const y = local.y - SUBJECT_Y;
      const z = local.z;
      const h = clampHorizontalAngle(Math.round((Math.atan2(x, z) * 180) / Math.PI));
      const v = clampVerticalAngle(Math.round((Math.asin(Math.max(-1, Math.min(1, y / ORBIT_RADIUS))) * 180) / Math.PI));
      // 位姿立即本地应用（不经过 React 回路），拖拽跟手
      const entry = lightEntriesRef.current.get(drag.lightId);
      const cur = lightsRef.current.find((l) => l.id === drag.lightId);
      if (entry && cur) {
        applyLightPose(threeRef.current!, entry, { ...cur, horizontalAngle: h, verticalAngle: v });
        requestRender();
      }
      // 数值回显与面板同步按 DRAG_EMIT_INTERVAL 节流，松手时补发最终值
      drag.pending = { lightId: drag.lightId, h, v };
      const now = performance.now();
      if (now - drag.lastEmit >= DRAG_EMIT_INTERVAL) {
        drag.lastEmit = now;
        onLightAngleChangeRef.current(drag.lightId, h, v);
        drag.pending = undefined;
      }
    }
  }, [ready, hitTestBulb, toNDC, requestRender]);

  const endDrag = useCallback((e: React.PointerEvent) => {
    const container = containerRef.current;
    if (container && container.hasPointerCapture(e.pointerId)) {
      container.releasePointerCapture(e.pointerId);
    }
    dragRectRef.current = null;
    // 松手补发最终值，保证 React 状态与 3D 位姿一致（防撤销/生成取到中间值）
    const drag = dragStateRef.current;
    if (drag?.mode === 'light' && drag.pending) {
      onLightAngleChangeRef.current(drag.pending.lightId, drag.pending.h, drag.pending.v);
    } else if (drag?.mode === 'rotate' && drag.pendingRot !== undefined) {
      onSceneRotationChangeRef.current(drag.pendingRot);
    }
    dragStateRef.current = null;
  }, []);

  // 滚轮：视口推拉（nowheel 已阻断画布缩放）
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !ready) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      distRef.current = Math.max(CAMERA_DIST_MIN, Math.min(CAMERA_DIST_MAX, distRef.current * (1 + e.deltaY * 0.001)));
      applyCameraPose();
      requestRender();
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [ready, applyCameraPose, requestRender]);

  const isDraggingLight = dragStateRef.current?.mode === 'light';

  return (
    <div
      ref={containerRef}
      className="nodrag nowheel relative h-full w-full overflow-hidden rounded-lg select-none"
      style={{ background: '#0a0a0b', touchAction: 'none', cursor: isDraggingLight ? 'grabbing' : hoverBulb ? 'grab' : 'default' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <div ref={canvasHostRef} className="absolute inset-0" />
      {/* 顶部操作提示（浮层，不拦截指针） */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-md bg-black/45 text-[10px] text-white/55 whitespace-nowrap pointer-events-none">
        左键拖拽旋转场景 · 点击灯光拖拽调整位置
      </div>
      {textureFailed && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <ImageOff size={20} className="text-white/25" />
        </div>
      )}
    </div>
  );
});
